import Box from "../Box";
import AlertRequestQuery from "~/entities/AlertRequestQuery";
import ValidationModule from "~/entities/ValidationModule";

export default class ValidationDetails extends ValidationModule {
  public addressAlerts = {
    selector: {
      fedEx: "AlertShipToFedEx",
      ups: "AlertShipToUPS",
      upsFrom: "AlertShipFromUPS",
      poBox: "POAddressWarning",
      poFromBox: "POFromAddressWarning",
      companyName: "AlertCompanyNameTooLong",
      address2: "address2Required",
    },
    needle: [""],
  };

  public requote = {
    selector: {
      RequoteFailure: "RequoteFailure",
      LocationChangedRequote: "LocationChangedRequote",
      LocationChangedRequoteWihtoutChanges:
        "LocationChangedRequoteWihtoutChanges",
      RateDiffValue: "RateDiffValue",
    },
    needle: [""],
  };

  public contentAlerts = {
    selector: {
      USeBike: "AlertEbikeShipment",
      eBike: "AlertEBikeInternational",
    },
    needle: [""],
  };

  public rateAlerts = {
    selector: {
      default: "AlertFedExFailed",
    },
    needle: [""],
  };

  public constructor() {
    super("Details");
    this.alertNames = [
      "AlertInvalidFromCountry", // We do not currently offer shipping services from #CountryName#
      "AlertInvalidCountry", // We do not currently offer shipping services to #CountryName#.
      "MultiplePackageHighValue", // Some of the packages in your shipment qualify for Preferred Handling. To ensure the whole shipment receives the same service level, we suggest you select Protection levels for each that include Preferred Handling.
      "AlertPostalCodeLookup", // Unable to lookup your postal code right now, no problem though.
      "AlertEbikeShipment", // BikeFlights’ e-bike shipping program is for safely shipping single e-bikes that have their battery either contained within the frame or fully installed.
      "AlertEBikeInternational", // E-bike shipping is currently available via ground service within the contiguous U.S.
      "AlertFedExFailed", // We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city, state, and zip code combination to get rates. Thank you.
      "AlertFedExCanadaNoResults", // Sorry! We were not able to get rates from the information you provided. Typically the reason is that you gave us too much or too little detail. Try typing something as simple as a basic street address or a city and state combination to get rates. Thank you.
      "address2Required",

      "RequoteFailure", // Unable to update your quote with the current country and postal code, Please update and try again or go back to the home page.
      "Ship3Submit", // <strong>Congrats!</strong> You created a shipment.
      "AlertShipToFedEx", // FedEx locations will not accept packages shipped via UPS.  Please contact Support for the location of a UPS store near you.'
      "POAddressWarning", // BikeFlights.com does not offer shipping to a  P.O.Box, APO and FPO address. Some of our partner carriers do offer a special service that transfers the package to postal service for final delivery, however, bicycles often exceed the size and weight restrictions of the special service and the transfer time between carrier and postal service has been unreliable.
      "AlertShipToUPS", // Please contact this UPS Store prior to booking your shipment to make sure they will accept and hold your shipment and to learn of any additional charges that you may have to pay them for this service. Thank you.
      "AlertShipFromUPS", // When shipping from a UPS Store, please Affix the Label to the shipment yourself and get a drop off receipt!
      "POFromAddressWarning", // BikeFlights.com does not offer shipping from a P.O.Box, APO and FPO address. Some of our partner carriers do offer a special service that transfers the package to postal service for final delivery, however, bicycles often exceed the size and weight restrictions of the special service and the transfer time between carrier and postal service has been unreliable.
      "LocationChangedRequote", //
      "AlertEbikeShipment", // BikeFlights’ e-bike shipping program is for safely shipping single e-bikes that have their battery either contained within the frame or fully installed.
      "AlertEBikeInternational", // E-bike shipping is currently available via ground service within the contiguous U.S.
      "AlertCompanyNameTooLong", // To prevent delays and problems with your shipment, your company name will be limited to 35 characters in the shipment label.
      "LocationChangedRequoteWihtoutChanges",
      "RateDiffValue",
    ];
    this.alertTypes = {
      // fill
      requote: this.requote,
      address: this.addressAlerts,
      content: this.contentAlerts,
      rates: this.rateAlerts,
    };
  }

  public async ValidateAddress2(scope: string = "") {
    await this.component?.checkIfAVSRequireAddress2();
    const anyPropertyIsTrue = this.component.address2Required.some((obj) =>
      Object.values(obj).includes(true)
    );
    if (anyPropertyIsTrue) {
      this.requestAlert({ name: "address2", type: "address" });
    }
  }

  public async Validate(scope: string = "") {
    // for later Validate needs some kind of flag to asks for a particular validation type, depth or scope. so that only that method is called
    this.resetValidations();
    let errors = 0;
    for (let step = 0; step < this.component?.stageDetails.length; step++) {
      const from = this.component?.stageDetails[step].from;
      const to = this.component?.stageDetails[step].to;
      if (!from.isFilled()) {
        errors++;
        this.validationErrors.push("from-" + step);
      } else if (!from.phoneValid) {
        errors++;
        this.validationErrors.push("from-phone-" + step);
      }

      if (!to.isFilled()) {
        errors++;
        this.validationErrors.push("to-" + step);
      } else if (!to.phoneValid) {
        errors++;
        this.validationErrors.push("to-phone-" + step);
      }

      if (
        !errors &&
        this.detailsFindConditional(this.component?.stageDetails[step])
      ) {
        errors++;
        this.validationErrors.push("LocationChangedRequote");
        // needs to update details values so rates can evaluate on new values and fail
        this.component?.updateStage();
        this.component?.setDetails(
          JSON.parse(JSON.stringify(this.component?.stageDetails))
        );
        // this.component?.requestRates({stepName: 'Costs', stepNumber: 2});
      }
    }
    errors += this.validateAddressFields();
    if (errors === 0) {
      this.validated = true;
      this.validationMade = true;
    } else {
      this.validated = false;
      this.validationMade = true;
    }
    return !errors;
  }

  public resetValidations() {
    this.validated = false;
    this.validationMade = false;
    this.validationErrors = [];
    this.component?.clearActiveNotices();
  }

  public requestAlert(data: any) {
    this.component?.requestAlert(this.getFilteredAlertKey(data, "asObject"));
  }

  public addNotice(data: any) {
    this.component?.addNotice(this.getFilteredAlertKey(data));
  }

  public removeNotice(data: any) {
    this.component?.removeNotice(this.getFilteredAlertKey(data));
  }

  protected getFilteredAlertKey(
    data: any,
    returnType: string = "asString"
  ): any {
    const keyGetter = new AlertRequestQuery();
    if (data.hasOwnProperty("type")) {
      switch (data.type) {
        case "requote": {
          let name = "";
          const event = data.event;
          if (event) {
            if (event.hasOwnProperty("results")) {
              name = event.results;
            } else {
              name = "RequoteFailure";
            }
          }
          keyGetter.name = this.alertTypes[data.type].selector[name];
          keyGetter.typeSelector = name;
          break;
        }
        case "address":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "content":
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
        case "rates":
          keyGetter.name = this.alertTypes[data.type].selector["default"];
          keyGetter.typeSelector = "default";
          3;
          break;
        case "address2":
          keyGetter.name =
            this.alertTypes[data.type].selector["address2Required"];
          keyGetter.typeSelector = "address2Required";
          break;
      }
    } else {
      // fill
    }
    const filteredData = keyGetter.getKey(
      this.alertNames,
      this.alertTypes[data.type]
    );
    if (returnType === "asString") {
      return filteredData.name;
    } else {
      return filteredData;
    }
  }

  validatePackageSelection() {
    const boxes = this.component?.boxes;

    boxes?.forEach((box: Box, index: number) => {
      this.component?.cleanBoxErrors(index);
      // This box Size Values
      const thisContent = box.contents;
      // Validate E-bike notice
      if (!thisContent || !thisContent.label) {
        if (this.component && typeof this.component.setBoxError === "function")
          this.component.setBoxError({
            index,
            error: "categorySelectionInvalid",
          });
        this.validationErrors.push("categorySelectionInvalid");
      } else if (
        thisContent &&
        thisContent.label &&
        thisContent.label.includes("E-Bike")
      ) {
        const countries = this.component?.currentCountries;
        let nonUS = false;
        for (const property in countries) {
          if (countries[property] !== "US") {
            nonUS = true;
          }
        }
        if (nonUS) {
          this.requestAlert({ name: "eBike", type: "content" });
        } else {
          this.requestAlert({ name: "USeBike", type: "content" });
        }
      }
    });
  }

  public validatePhoneNumber(phone) {
    phone = String(phone);
    const validatePhoneString = phone?.replace(/[#+-.() ]/g, "");
    return validatePhoneString && validatePhoneString.length > 5;
  }

  public detailsFindConditional(elem) {
    return elem.to.requote === true || elem.from.requote === true;
  }

  public validateAddressFields() {
    this.resetValidations();
    const component = this.component;
    let errors = 0;
    if (component) {
      for (let step = 0; step < component.stageDetails.length; step++) {
        const address = component.stageDetails[step];

        const isFromFedEx = this.isFedExOffice(address.from.company);
        const isFromUps = this.isUpsOffice(address.from.company);
        const isFromPoBox = this.isPoBox(address.from);
        const fromIsTooLong = this.isLongCompany(address.from.company);

        const isToFedEx = this.isFedExOffice(address.to.company);
        const isToUps = this.isUpsOffice(address.to.company);
        const isToPoBox = this.isPoBox(address.to);
        const toIsTooLong = this.isLongCompany(address.to.company);

        if (isFromFedEx || isToFedEx) {
          this.requestAlert({ name: "fedEx", type: "address" });
        }
        if (isFromUps) {
          this.requestAlert({ name: "upsFrom", type: "address" });
        }
        if (isToUps) {
          this.requestAlert({ name: "ups", type: "address" });
        }
        if (isToPoBox) {
          this.validationErrors.push("poBox");
          errors++;
          this.requestAlert({ name: "poBox", type: "address" });
        }
        if (isFromPoBox) {
          this.validationErrors.push("poFromBox");
          errors++;
          this.requestAlert({ name: "poFromBox", type: "address" });
        }

        if (fromIsTooLong || toIsTooLong) {
          this.requestAlert({ name: "companyName", type: "address" });
        }
      }
    }
    return errors;
  }

  public isFedExOffice(companyName) {
    if (
      companyName !== null &&
      typeof companyName !== "undefined" &&
      companyName !== ""
    ) {
      if (companyName.toLowerCase().indexOf("fedex") > -1) {
        return true;
      }
    }
    return false;
  }

  public isUpsOffice(companyName) {
    if (
      companyName !== null &&
      typeof companyName !== "undefined" &&
      companyName !== ""
    ) {
      const lowerCompany = companyName.toLowerCase();
      if (
        lowerCompany.indexOf("ups store") > -1 ||
        lowerCompany.indexOf("united state postal service") > -1 ||
        lowerCompany.indexOf("united state postal office") > -1 ||
        lowerCompany.indexOf("united states postal service") > -1 ||
        lowerCompany.indexOf("united states postal office") > -1 ||
        lowerCompany.indexOf("us post office") > -1 ||
        lowerCompany.indexOf("us postal office") > -1 ||
        lowerCompany.indexOf("us post service") > -1 ||
        lowerCompany.indexOf("us postal service") > -1
      ) {
        return true;
      }
    }
    return false;
  }

  public isPoBox(address) {
    if (typeof address === "undefined" || address === null) {
      return false;
    }
    // check for PO Box
    if (typeof address.address1 === "undefined" || address.address1 === null) {
      return false;
    }
    let hasPoBox = address.streetAddress.match(/p\.?o\.?\s?box/i);
    if (hasPoBox) {
      return true;
    }
    hasPoBox = address.streetAddress.match(/post\s?office\s?box/i);
    if (hasPoBox) {
      return true;
    }
    hasPoBox = address.streetAddress.match(/us\s?post\s?office/i);
    if (hasPoBox) {
      return true;
    }
    hasPoBox = address.streetAddress.match(/united\s?state\s?postal/i);
    if (hasPoBox) {
      return true;
    }
    hasPoBox =
      address.streetAddress &&
      address.streetAddress.toLowerCase().indexOf("box") === 0;
    if (hasPoBox) {
      return true;
    }
    if (typeof address.address2 === "undefined" || address.address2 === null) {
      return false;
    }
    hasPoBox = address.address2.match(/p\.?o\.?\s?box/i);
    if (hasPoBox) {
      return true;
    }
    hasPoBox = address.address2.match(/post\s?office\s?box/i);
    if (hasPoBox) {
      return true;
    }
    // check for APO
    if (typeof address.city === "undefined" || address.city === null) {
      return false;
    }
    const hasApoBox = address.city.match(/^apo$/i);
    if (hasApoBox) {
      return true;
    }
    // check for FPO
    const hasFpoBox = address.city.match(/^fpo$/i);
    return !!hasFpoBox;
  }

  public isLongCompany(companyName) {
    if (
      companyName !== null &&
      typeof companyName !== "undefined" &&
      companyName !== ""
    ) {
      if (companyName.length > 34) {
        return true;
      }
    }
    return false;
  }
}
